import { Replayer } from 'rrweb';
import { buildTimeline } from './timeline.js';
import { REPLAY_ANIMATION_RESET } from './replayStyle.js';
/**
 * Framework-agnostic replay engine. Wraps rrweb's `Replayer` — which already
 * provides pixel-accurate DOM reconstruction, animated mouse cursor, click
 * ripples, and scroll animation — behind a small video-like control surface
 * (play/pause/seek/speed) plus a derived {@link ReplayTimeline} of markers.
 *
 * Our recorded events are rrweb-compatible (identical EventType/Source
 * numbering), so they feed the Replayer directly with no transformation.
 */
export class ReplayEngine {
    replayer;
    timeline;
    speed = 1;
    playing = false;
    raf = 0;
    listeners = new Set();
    constructor(events, root) {
        this.timeline = buildTimeline(events);
        // rrweb's Replayer types its own eventWithTime; our schema is structurally
        // identical (that is the whole point of the shared numbering).
        this.replayer = new Replayer(events, {
            root,
            speed: this.speed,
            // Fast-forward through idle gaps (no events) so playback doesn't appear
            // frozen on a second while nothing is happening — like Clarity.
            skipInactive: true,
            // Shorten the idle gap before skipping kicks in (rrweb default is 10s).
            inactivePeriodThreshold: 3000,
            mouseTail: { strokeStyle: '#e11d48', lineWidth: 3 },
            showWarning: false,
            liveMode: false,
            // Content gated behind CSS entrance animations (opacity:0 → 1) would
            // otherwise render blank when it's already present in the first snapshot —
            // see REPLAY_ANIMATION_RESET.
            insertStyleRules: REPLAY_ANIMATION_RESET,
        });
        this.replayer.on('finish', () => {
            this.playing = false;
            this.emit();
        });
    }
    getTimeline() {
        return this.timeline;
    }
    /**
     * The rrweb replay iframe, whose `contentDocument` holds the reconstructed
     * page. Exposed so callers (tester mode) can screenshot the actual frame
     * rather than the outer wrapper.
     */
    getIframe() {
        return this.replayer.iframe ?? null;
    }
    play(atMs) {
        // rrweb's getCurrentTime() returns a large NEGATIVE value before the first
        // play (baseline − firstTimestamp), so passing it straight to play() would
        // start from a negative offset and never advance. Clamp to [0, duration].
        const from = Math.max(0, Math.min(atMs ?? this.currentTime(), this.timeline.durationMs));
        this.replayer.play(from);
        this.playing = true;
        this.tick();
        this.emit();
    }
    pause() {
        this.replayer.pause();
        this.playing = false;
        cancelAnimationFrame(this.raf);
        this.emit();
    }
    toggle() {
        this.playing ? this.pause() : this.play();
    }
    /** Seek to an absolute offset (ms from start), preserving play/pause state. */
    seek(offsetMs) {
        const clamped = Math.max(0, Math.min(offsetMs, this.timeline.durationMs));
        if (this.playing)
            this.replayer.play(clamped);
        else
            this.replayer.pause(clamped);
        this.emit();
    }
    /** Jump to a timeline marker index. */
    jumpToMarker(index) {
        const marker = this.timeline.markers[index];
        if (marker)
            this.seek(marker.offsetMs);
    }
    setSpeed(speed) {
        this.speed = speed;
        this.replayer.setConfig({ speed });
        this.emit();
    }
    on(listener) {
        this.listeners.add(listener);
        // Push current state immediately so the UI shows duration/position before
        // the first play/seek interaction.
        listener({
            playing: this.playing,
            currentTimeMs: Math.max(0, this.currentTime()),
            durationMs: this.timeline.durationMs,
            speed: this.speed,
        });
        return () => this.listeners.delete(listener);
    }
    destroy() {
        cancelAnimationFrame(this.raf);
        this.replayer.pause();
        this.listeners.clear();
    }
    currentTime() {
        return this.replayer.getCurrentTime();
    }
    /** Last listener notification, for throttling the playback ticker. */
    lastEmitAt = 0;
    /**
     * Listener updates drive React state in the host page — at rAF rate (~60/s)
     * the whole replay screen re-renders every frame and playback visibly
     * stutters on marker-heavy sessions. 10 Hz keeps the clock and playhead
     * feeling live at a fraction of the render cost.
     */
    static EMIT_INTERVAL_MS = 100;
    tick = () => {
        if (!this.playing)
            return;
        const now = performance.now();
        if (now - this.lastEmitAt >= ReplayEngine.EMIT_INTERVAL_MS) {
            this.lastEmitAt = now;
            this.emit();
        }
        this.raf = requestAnimationFrame(this.tick);
    };
    emit() {
        const state = {
            playing: this.playing,
            // Clamp to [0, duration] — rrweb's clock can drift slightly past the last
            // event, which would otherwise show e.g. "0:32 / 0:27".
            currentTimeMs: Math.max(0, Math.min(this.currentTime(), this.timeline.durationMs)),
            durationMs: this.timeline.durationMs,
            speed: this.speed,
        };
        for (const l of this.listeners)
            l(state);
    }
}
//# sourceMappingURL=engine.js.map